import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { Search, FilterList as FilterIcon } from "@material-ui/icons";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateUtils from "@date-io/moment";
import axios from "axios";
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import Card from "../../components/Card/Card.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CardBody from "../../components/Card/CardBody.js";
import CustomInput from "../../components/CustomInput/CustomInput";
import { GetAllBrandsAPI, GetAllProdLogAPI, PrintProdLogPDFAPI } from "../../config/api";
import { toast } from "react-toastify";
import PrintIcon from "@mui/icons-material/Print";
import { Autocomplete, createFilterOptions } from "@mui/material";
import CircularProgress from '@material-ui/core/CircularProgress';
// import { Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, Button, Autocomplete } from '@mui/material';
// import { DatePicker } from '@mui/lab';

const useStyles = makeStyles((theme) => ({
  cardTitleWhite: {
    color: "#FFFFFF",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
  },
  filterBar: {
    padding: theme.spacing(2, 0),
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    marginBottom: theme.spacing(2),
  },
  tableWrapper: {
    overflowX: "auto",
    minHeight: 320,
  },
}));

// Helper function to remove first 3 digits from product code
const removeFirstThreeDigits = (productCode) => {
  if (!productCode || productCode.length <= 3) return productCode || "";
  return productCode.substring(3);
};

const normalizeToDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === "object") {
    if (typeof value.toDate === "function") {
      const parsed = value.toDate();
      return isNaN(parsed?.getTime()) ? null : parsed;
    }

    if (value.$d instanceof Date) {
      return isNaN(value.$d.getTime()) ? null : value.$d;
    }

    if (typeof value.valueOf === "function") {
      const parsed = new Date(value.valueOf());
      return isNaN(parsed.getTime()) ? null : parsed;
    }
  }

  return null;
};

const columns = [
  {
    id: "productCode",
    label: "Product Code",
    format: (value) => removeFirstThreeDigits(value),
  },
  { id: "productName", label: "Product Name" },
  {
    id: "price",
    label: "Price",
    format: (value) =>
      Number(value).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
        style: "currency",
        currency: "INR",
      }),
  },
  { id: "quantity", label: "Quantity" },
  { id: "status", label: "Status" },
];

const filterOptions = createFilterOptions({
  matchFrom: "any",
  limit: 500,
});

export default function ProdLogList() {
  const classes = useStyles();
  const authToken = useSelector((state) => state.userReducer.authToken);
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [openFilter, setOpenFilter] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brand, setBrand] = useState([]);
  const [color, setColor] = useState("");
  const userInfo = useSelector((state) => state.userReducer.userInfo);
  const [loading, setLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: null,
    endDate: null,
    brand: null,
    color: "",
    search: "",
  });

  useEffect(() => {
    fetchBrands();
  }, []); // Only fetch brands once on mount

  useEffect(() => {
    fetchProductLogs(page, rowsPerPage, appliedFilters);
  }, [page, rowsPerPage, appliedFilters]);

  const fetchBrands = async () => {
    try {
      const res = await axios.get(GetAllBrandsAPI(userInfo.entity), {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setBrand(res.data.brandMasters);
    } catch (err) {
      toast.error("Something went wrong, Please Contact Admin");
    }
  };

  // Helper function to enrich ProductLog data with missing fields
  // NOTE: This is a temporary workaround. Backend should include sph, cyl, axis, addition in API response.
  const enrichProductLogData = (productLogs) => {
    return productLogs.map(item => {
      // If power fields are missing, set empty strings (backend should provide these)
      // The backend aggregation should preserve these fields using $first in $group stage
      return {
        ...item,
        sph: item.sph ?? "",
        cyl: item.cyl ?? "",
        axis: item.axis ?? "",
        addition: item.addition ?? "",
      };
    });
  };

  const fetchProductLogs = async (page, rowsPerPage, filters = {}) => {
    setLoading(true);
    const params = [`page=${page}`, `limit=${rowsPerPage}`];

    if (userInfo?.entity) {
      params.push(`entity=${userInfo.entity}`);
    }

    if (filters.startDate && filters.endDate) {
      const start = normalizeToDate(filters.startDate);
      const end = normalizeToDate(filters.endDate);
      if (start && end) {
        params.push(`startDate=${start.toISOString()}`);
        params.push(`endDate=${end.toISOString()}`);
      }
    }
    if (filters.brand) {
      const brandName = filters.brand.name || filters.brand;
      params.push(`brandName=${encodeURIComponent(brandName)}`);
    }
    if (filters.color) {
      params.push(`color=${encodeURIComponent(filters.color)}`);
    }
    if (filters.search) {
      params.push(`search=${encodeURIComponent(filters.search)}`);
    }

    const query = `?${params.join("&")}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    try {
      const res = await axios.get(`${GetAllProdLogAPI()}${query}`, {
        headers: { Authorization: `Bearer ${authToken}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      let productLogs = res.data.productLog || [];
      productLogs = enrichProductLogData(productLogs);
      setAccounts(productLogs);
      setAllAccounts(productLogs);
      setTotal(res.data.total || 0);
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError' || err.code === 'ECONNABORTED') {
        toast.error("Request timeout - Server is taking too long to respond. Please contact admin.");
      } else if (err.response?.status === 404) {
        toast.error("No product logs found");
        setAccounts([]);
        setAllAccounts([]);
        setTotal(0);
      } else {
        console.error("Error fetching product logs:", err);
        toast.error("Failed to fetch product logs. Please try again or contact admin.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    const start = normalizeToDate(startDate);
    const end = normalizeToDate(endDate);
    const hasDateRange = Boolean(start && end);
    const rangeStart = !hasDateRange || (start && end && start <= end) ? start : end;
    const rangeEnd = !hasDateRange || (start && end && start <= end) ? end : start;

    const newFilters = {
      ...appliedFilters,
      startDate: hasDateRange ? rangeStart : null,
      endDate: hasDateRange ? rangeEnd : null,
      brand: selectedBrand,
      color: color,
      search: (typeof searchInput === "string" ? searchInput : "") || appliedFilters.search || "",
    };

    setAppliedFilters(newFilters);
    setOpenFilter(false);
  };

  const handleApplyDateRange = () => {
    const start = normalizeToDate(startDate);
    const end = normalizeToDate(endDate);
    const hasDateRange = Boolean(start && end);
    const rangeStart = !hasDateRange || start <= end ? start : end;
    const rangeEnd = !hasDateRange || start <= end ? end : start;

    setAppliedFilters((prev) => ({
      ...prev,
      startDate: hasDateRange ? rangeStart : null,
      endDate: hasDateRange ? rangeEnd : null,
    }));
  };

  const handleResetDateRange = () => {
    setStartDate(null);
    setEndDate(null);
    setAppliedFilters((prev) => ({
      ...prev,
      startDate: null,
      endDate: null,
    }));
  };

  const handleSearch = () => {
    // Update applied filters with search term
    const newFilters = {
      ...appliedFilters,
      search: searchInput,
    };
    setAppliedFilters(newFilters);
  };

  // Use accounts directly since filtering is now done on backend
  const filteredAccounts = accounts;
  console.log(filteredAccounts, "<<<<filteredAccounts")

  async function _onPreview() {
    try {
      setLoading(true);
      const params = [];

      if (userInfo?.entity) {
        params.push(`entity=${encodeURIComponent(userInfo.entity)}`);
      }

      if (appliedFilters.startDate && appliedFilters.endDate) {
        const start = normalizeToDate(appliedFilters.startDate);
        const end = normalizeToDate(appliedFilters.endDate);
        if (start && end) {
          params.push(`startDate=${encodeURIComponent(start.toISOString())}`);
          params.push(`endDate=${encodeURIComponent(end.toISOString())}`);
        }
      }

      if (appliedFilters.brand) {
        const brandName = appliedFilters.brand.name || appliedFilters.brand;
        params.push(`brandName=${encodeURIComponent(brandName)}`);
      }
      if (appliedFilters.color) {
        params.push(`color=${encodeURIComponent(appliedFilters.color)}`);
      }
      if (appliedFilters.search) {
        params.push(`search=${encodeURIComponent(appliedFilters.search)}`);
      }

      const endpoint = `${PrintProdLogPDFAPI()}?${params.join("&")}`;
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
          Accept: "application/pdf,application/octet-stream,application/json",
        },
      });

      if (!response.ok) {
        let serverMessage = "Failed to generate PDF. Please try again.";
        try {
          const contentType = response.headers.get("content-type") || "";
          const errorText = await response.text();
          if (errorText) {
            if (contentType.includes("application/json")) {
              const parsed = JSON.parse(errorText);
              serverMessage = parsed?.error || parsed?.message || serverMessage;
            } else {
              try {
                const parsed = JSON.parse(errorText);
                serverMessage = parsed?.error || parsed?.message || errorText;
              } catch {
                serverMessage = errorText;
              }
            }
          }
        } catch {
          // keep fallback
        }
        throw new Error(serverMessage);
      }

      const pdfArrayBuffer = await response.arrayBuffer();
      if (!pdfArrayBuffer || pdfArrayBuffer.byteLength === 0) {
        throw new Error("Received empty PDF from server.");
      }

      const pdfBlob = new Blob([pdfArrayBuffer], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      const opened = window.open(url, "_blank");
      if (!opened) {
        toast.error("Popup blocked. Please allow popups and try again.");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success("Product log PDF generated successfully.");
    } catch (error) {
      console.error("Error fetching product log PDF:", error);

      let errorMessage = "Failed to generate PDF. Please try again.";
      const rawErrorMessage =
        error?.message ||
        error?.toString?.() ||
        "";

      if (
        rawErrorMessage.includes("Intercepted by the IDM Advanced Integration") ||
        rawErrorMessage.includes("ERR_FAILED") ||
        rawErrorMessage.includes("Failed to fetch")
      ) {
        toast.error(
          "PDF request was intercepted by IDM. Disable IDM browser integration for localhost and try again."
        );
        return;
      }

      if (error?.message) errorMessage = error.message;

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  const handleClearFilter = () => {
    // Clear filter input states
    setStartDate(null);
    setEndDate(null);
    setColor("");
    setSelectedBrand(null);
    setSearchInput("");
    // Clear applied filters and reset page
    setAppliedFilters({
      startDate: null,
      endDate: null,
      brand: null,
      color: "",
      search: "",
    });
    setOpenFilter(false);
  };

  return (
    <>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="primary">
              <Grid container alignItems="center">
                <Grid item xs={12} md={6}>
                  <h4 className={classes.cardTitleWhite}>Product Logs</h4>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center">
                    <Box style={{ maxWidth: 320, width: "100%", marginRight: 16 }}>
                      <CustomInput
                        id="search"
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                          placeholder: "Search product codes, names...",
                          value: searchInput,
                          onChange: (e) => setSearchInput(e.target.value),
                          onKeyDown: (e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSearch();
                            }
                          },
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={handleSearch}
                                edge="end"
                                aria-label="Search"
                              >
                                <Search />
                              </IconButton>
                            </InputAdornment>
                          ),
                          style: { color: "white" },
                        }}
                      />
                    </Box>
                    <Tooltip title="More filters (date, brand, color)">
                      <IconButton
                        color="inherit"
                        onClick={() => setOpenFilter(true)}
                        aria-label="Filters"
                      >
                        <FilterIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print PDF">
                      <IconButton
                        color="inherit"
                        onClick={_onPreview}
                        disabled={loading}
                        aria-label="Print PDF"
                      >
                        <PrintIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </CardHeader>
            <CardBody>
              <div className={classes.tableWrapper}>
              <Table
                stickyHeader
                aria-label="sticky table"
                style={{ width: "100%" }}
              >
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell key={column.id} align="center">
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={columns.length} align="center" style={{ padding: 48 }}>
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : filteredAccounts.length > 0 ? (
                      filteredAccounts.map((row, idx) => (
                        <TableRow hover key={row._id || `${row.productCode}-${idx}`}>
                          {columns.map((column) => (
                            <TableCell key={column.id} align="center">
                              {column.format
                                ? column.format(row[column.id])
                                : row[column.id]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length} align="center" style={{ padding: 48 }}>
                          No records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(+event.target.value);
                  setPage(0);
                }}
              />
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      <Dialog open={openFilter} onClose={() => setOpenFilter(false)} fullWidth maxWidth="sm">
        <DialogTitle style={{ fontWeight: 600, fontSize: "1.2rem" }}>
          More Filters
        </DialogTitle>
        <DialogContent dividers>
          <MuiPickersUtilsProvider utils={DateUtils}>
            <Box display="flex" flexDirection="column" style={{ gap: 24 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="From"
                    value={startDate}
                    onChange={setStartDate}
                    format="DD-MM-YYYY"
                    fullWidth
                    autoOk
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="To"
                    value={endDate}
                    onChange={setEndDate}
                    format="DD-MM-YYYY"
                    fullWidth
                    autoOk
                  />
                </Grid>
              </Grid>
              <TextField
                label="Color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                fullWidth
                variant="outlined"
                margin="normal"
              />
              <Autocomplete
                filterOptions={filterOptions}
                loading={!brand.length}
                options={brand}
                value={selectedBrand}
                onChange={(event, newValue) => setSelectedBrand(newValue)}
                getOptionLabel={(option) => option.name || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brand"
                    fullWidth
                    variant="outlined"
                    margin="normal"
                  />
                )}
              />
            </Box>
          </MuiPickersUtilsProvider>
        </DialogContent>
        <DialogActions style={{ padding: 16, justifyContent: "space-between" }}>
          <Button onClick={handleClearFilter} variant="outlined" color="primary">
            Clear All
          </Button>
          <Box display="flex" style={{ gap: 16 }}>
            <Button onClick={() => setOpenFilter(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleApplyFilter} variant="contained" color="primary">
              Apply
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
